import { Suspense, lazy, useEffect, useState } from "react";
import { useGlobalReducer } from "../../../GlobalContext";
import { FieldValue } from "../../blocks/fields/FieldValue";
import { TextField } from "../../blocks/fields/TextField";
import { RecordTable } from "../../blocks/layout/RecordTable";
import { RecordTableColumn } from "../../blocks/layout/RecordTable.types";
import { TablePagination } from "../../blocks/layout/TablePagination";
import { toDisplayDateTime } from "../../blocks/fields/formatDate";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Button } from "../../elements/Button";
import { ConfirmationModal } from "../../elements/ConfirmationModal";
import { Loading } from "../../elements/Loading";
import { DeleteIcon } from "../../icons";
import "../../lists/ListRowActions.scss";
import { useEntityPage } from "../useEntityPage";
import { onMarketingListMembersChanged } from "./marketingListMembersChanged";
import { removeContactsFromList } from "./marketingListMembership";
import { useMarketingListMembers } from "./useMarketingListMembers";
import { MarketingListMember } from "./useMarketingListMembers.types";

// Lazy so the registry that imports this page does not bring the router in with it.
const RecordLink = lazy(() =>
  import("../../blocks/layout/RecordLink").then((module) => ({
    default: module.RecordLink,
  }))
);

// The service sends these already in local time, so they are shown as they are.
const showDateTime = (value: unknown) =>
  toDisplayDateTime(value as string);

const ACTIONS_COLUMN_KEY = "__actions";

const buildColumns = (
  onRemove?: (member: MarketingListMember) => void
): RecordTableColumn<MarketingListMember>[] => [
  {
    key: "name",
    label: "Name",
    width: 300,
    sortable: true,
    accessor: (member) => (
      <Suspense fallback={member.Name}>
        <RecordLink
          to="/contact"
          id={member.Id}
          text={member.Name}
          newTab="button"
        />
      </Suspense>
    ),
  },
  {
    key: "emailaddress1",
    label: "Email Address",
    width: 300,
    sortable: true,
    accessor: (member) => String(member.Fields?.emailaddress1 ?? ""),
  },
  {
    key: "dateadded",
    label: "Date Added",
    width: 200,
    sortable: true,
    accessor: (member) => showDateTime(member.Fields?.dateadded),
  },
  {
    key: "createdon",
    label: "Created On",
    width: 200,
    sortable: true,
    accessor: (member) => showDateTime(member.Fields?.createdon),
  },
  {
    key: "modifiedon",
    label: "Modified On",
    width: 200,
    sortable: true,
    accessor: (member) => showDateTime(member.Fields?.modifiedon),
  },
  ...(onRemove
    ? [
        {
          key: ACTIONS_COLUMN_KEY,
          label: "",
          width: 60,
          resizable: false,
          accessor: (member: MarketingListMember) => (
            <div className="d-flex justify-content-end">
              <button
                type="button"
                className="list-row-action hover-text-danger"
                title={`Remove ${member.Name} from the list`}
                aria-label={`Remove ${member.Name} from the list`}
                onClick={() => onRemove(member)}
              >
                <FontAwesomeIcon icon={DeleteIcon} />
              </button>
            </div>
          ),
        },
      ]
    : []),
];

/**
 * A marketing list's members, searched on the server a page at a time. A list can be
 * far too large to load whole, so this is not one of the record's related tables.
 */
export function MarketingListMembers() {
  const globalDispatch = useGlobalReducer();
  const { record, isNew, canEdit } = useEntityPage();
  const listId = record?.id ? String(record.id) : undefined;

  const [nameInput, setNameInput] = useState("");
  const [emailInput, setEmailInput] = useState("");
  const [applied, setApplied] = useState({ name: "", email: "" });
  const [sort, setSort] = useState<{ key: string; asc: boolean }>({
    key: "name",
    asc: true,
  });
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [searchToken, setSearchToken] = useState(0);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  // The members a confirmed Remove would take off; a name when it is one row.
  const [pendingRemoval, setPendingRemoval] = useState<{
    ids: string[];
    name?: string;
  } | null>(null);
  const [removing, setRemoving] = useState(false);

  const { members, total, loading, refresh } = useMarketingListMembers({
    listId: isNew ? undefined : listId,
    name: applied.name,
    email: applied.email,
    sortKey: sort.key,
    sortAscending: sort.asc,
    page,
    pageSize,
    searchToken,
  });

  // Members added from the section's Manage members button.
  useEffect(
    () =>
      onMarketingListMembersChanged(() => {
        setSelectedIds([]);
        refresh();
      }),
    [refresh]
  );

  if (isNew || !listId) {
    return (
      <p className="text-body-secondary mb-0">
        Save the list to add members to it.
      </p>
    );
  }

  const search = () => {
    setApplied({ name: nameInput.trim(), email: emailInput.trim() });
    setPage(1);
    setSelectedIds([]);
    setSearchToken((token) => token + 1);
  };

  const clear = () => {
    setNameInput("");
    setEmailInput("");
    setApplied({ name: "", email: "" });
    setPage(1);
    setSelectedIds([]);
    setSearchToken((token) => token + 1);
  };

  const toggleRow = (member: MarketingListMember) =>
    setSelectedIds((current) =>
      current.includes(member.Id)
        ? current.filter((id) => id !== member.Id)
        : [...current, member.Id]
    );

  const toggleAll = (rows: MarketingListMember[], selectAll: boolean) =>
    setSelectedIds((current) => {
      const onPage = rows.map((row) => row.Id);

      return selectAll
        ? Array.from(new Set([...current, ...onPage]))
        : current.filter((id) => !onPage.includes(id));
    });

  const remove = async () => {
    setRemoving(true);

    if (
      pendingRemoval &&
      (await removeContactsFromList(globalDispatch, listId, pendingRemoval.ids))
    ) {
      setPendingRemoval(null);
      setSelectedIds((current) =>
        current.filter((id) => !pendingRemoval.ids.includes(id))
      );
      refresh();
    }

    setRemoving(false);
  };

  return (
    <div>
      {/* Not a <form>: the record page already is one, and a submit that reached
          the browser would reload the page without its id. */}
      <div
        className="row gx-3 mb-2"
        onKeyDown={(event) => {
          if (event.key === "Enter") {
            event.preventDefault();
            search();
          }
        }}
      >
        <div className="col-12 col-md-4">
          <TextField
            label="Name"
            name="member-name"
            value={nameInput}
            onChange={setNameInput}
          />
        </div>
        <div className="col-12 col-md-4">
          <TextField
            label="Email Address"
            name="member-email"
            value={emailInput}
            onChange={setEmailInput}
          />
        </div>
        <div className="col-12 col-md-4">
          {/* A blank label gives the buttons the same top edge as the inputs. */}
          <FieldValue label={" "} htmlFor="member-search-actions">
            <div id="member-search-actions" className="d-flex gap-2">
              <Button
                text="Clear"
                variant="none"
                className="border rounded-2 bg-white text-dark fw-medium hover-bg-light"
                onClick={clear}
              />
              <Button
                text="Search members"
                variant="primary"
                onClick={search}
              />
            </div>
          </FieldValue>
        </div>
      </div>
      <p className="fs-7 fst-italic mb-2">
        Search the current members of this list. Email address must be an exact
        match.
      </p>
      {canEdit && selectedIds.length > 0 && (
        <div className="d-flex align-items-center gap-3 mb-2">
          <span>{selectedIds.length} selected</span>
          <Button
            text="Remove from list"
            variant="outline-danger"
            onClick={() => setPendingRemoval({ ids: selectedIds })}
          />
        </div>
      )}
      {loading ? (
        <Loading />
      ) : (
        <RecordTable
          columns={buildColumns(
            canEdit
              ? (member) =>
                  setPendingRemoval({ ids: [member.Id], name: member.Name })
              : undefined
          )}
          rows={members}
          className="mb-0"
          emptyMessage="No members found. Contacts are added to this list with Manage members."
          sortKey={sort.key}
          sortDir={sort.asc ? "asc" : "desc"}
          onSortChange={(key, dir) => {
            setSort({ key, asc: dir === "asc" });
            setPage(1);
          }}
          selection={
            canEdit
              ? {
                  selectedIds,
                  rowId: (member) => member.Id,
                  onToggleRow: toggleRow,
                  onToggleAll: toggleAll,
                  rowLabel: (member) => member.Name,
                }
              : undefined
          }
        />
      )}
      <TablePagination
        total={total}
        page={page}
        pageSize={pageSize}
        pageSizeId="member-page-size"
        onPageChange={setPage}
        onPageSizeChange={(size) => {
          setPageSize(size);
          setPage(1);
        }}
      />
      {pendingRemoval && (
        <ConfirmationModal
          modalTitle={
            pendingRemoval.ids.length === 1 ? "Remove member" : "Remove members"
          }
          confirmText="Remove"
          isSubmitting={removing}
          onClose={() => setPendingRemoval(null)}
          onConfirm={remove}
        >
          Are you sure you want to remove{" "}
          {pendingRemoval.name
            ? pendingRemoval.name
            : pendingRemoval.ids.length === 1
              ? "this member"
              : `these ${pendingRemoval.ids.length} members`}{" "}
          from the list?
        </ConfirmationModal>
      )}
    </div>
  );
}
